import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs/Observable";
import { ToastrService } from "ngx-toastr";
import {
  Tramite,
  Microproceso,
  HistorialMicroproceso,
  ConsultaTramite,
  EstructuraEspecifica,
  ValoresEspecificos,
  Filtro,
  Etapas,
} from "@app/shared";
import { Logger, EndpointService } from "@app/core";
import { observeOn } from "rxjs/operator/observeOn";

const log = new Logger("FlowService");

export interface TipoFiltro {
  id: number;
  nombre: string;
}

export interface LabelsSelector {
  alias: string;
  orden: number;
}

export interface EstructuraModal {
  key: string;
  label: string;
}

export interface Subestructura {
  labels_selector?: LabelsSelector[];
  estructura_modal?: EstructuraModal[];
  nombre_servicio?: string;
}

export interface Filter {
  id: number;
  label: string;
  alias: string;
  tipo_filtro: TipoFiltro;
  requerido: boolean;
  subestructura: Subestructura;
  tipo_estructura?: TipoFiltro;
}

export interface FiltrosSelector {
  id?: number;
  filtros?: Filter[];
  estructura?: any[];
  tramite_id?: any;
  envio_servicio?: any;
}

export interface DataService{
  tipo: string;
  valores: any[];
}



@Injectable()
export class FlowService {
  constructor(private http: HttpClient, private toastr: ToastrService, private api: EndpointService) {}

  /**
   * Retorna los tramites por default con sus actividades.
   *
   * @param {number} idDefaultTramite
   * @returns {Observable<ConsultaTramite[]>}
   * @memberof FlowService
   */
  getInstanceDefaultTramites(idDefaultTramite: number, filters?: any): Observable<ConsultaTramite[]> {
    return new Observable((obs) => {
      const dataForm = {
        id: idDefaultTramite,
      };
      if (filters) {
        dataForm["filtros"] = filters;
      }
      this.http.post<ConsultaTramite[]>(this.api.get("instanceDefaultTramite"), dataForm).subscribe(
        (instances) => {
          obs.next(instances);
          obs.complete();
        },
        (err) => {
          obs.error(err);
        }
      );
    });
  }

  /**
   * Retorna una instancia de un microproceso.
   *
   * @param {number} idTarea
   * @returns {Observable<Microproceso>}
   * @memberof FlowService
   */
  getMicroProcess(idTarea: number): Observable<Microproceso> {
    return new Observable((obs) => {
      this.http.get<Microproceso>(this.api.get("microprocesInstance", [idTarea])).subscribe(
        (instance) => {
          obs.next(instance);
          obs.complete();
        },
        (err) => {
          obs.error(err);
        }
      );
    });
  }

  /**
   * Retorna una instancia de un tramite por default.
   *
   * @param {number} idTramite
   * @returns {Observable<Tramite>}
   * @memberof FlowService
   */
  getInstance(idTramite: number): Observable<Tramite> {
    return new Observable((obs) => {
      this.http.get<Tramite>(this.api.get("instanceTramite", [idTramite])).subscribe(
        (instance) => {
          obs.next(instance);
          obs.complete();
        },
        (err) => {
          obs.error(err);
        }
      );
    });
  }

  /**
   * Crea una nueva intancia partiendo de un trámite por default.
   *
   * @param {number} idDefaultTramite
   * @param {boolean} dataLoad
   * @returns {Observable<number>}
   * @memberof FlowService
   */
  newInstance(idDefaultTramite: number, dataLoad: boolean): Observable<any> {
    const dataForm = {
      tramite: idDefaultTramite,
      carga: dataLoad,
    };
    return new Observable((obs) => {
      this.http.post<{ instancia_tramite: string }>(this.api.get("instanceTramite"), dataForm).subscribe(
        (data) => {
          const id = Number(data.instancia_tramite);
          obs.next(data);
          obs.complete();
          this.toastr.success(`El trámite fue creado correctamente con el código: ${id}`, "Operación exitosa");
        },
        (err) => {
          obs.error(err);
        }
      );
    });
  }

  /**
   * Consulta el historial de versiones de un microproceso
   *
   * @param {number} idTask
   * @returns {Promise<HistorialMicroproceso[]>}
   * @memberof FlowService
   */
  getHistoryMicroproces(idTask: number): Promise<HistorialMicroproceso[]> {
    return new Promise((resolve, reject) => {
      this.http.get<HistorialMicroproceso[]>(this.api.get("historyMicroproces", [idTask])).subscribe(
        (historyMicroproces) => {
          resolve(historyMicroproces);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  /**
   * Consulta los filtros para la consulta de el tramite
   *
   * @param {number} idTramite
   * @returns {Promise<Filtro>}
   * @memberof FlowService
   */
  getFiltersShearchTramite(idTramite: number): Promise<Filtro> {
    return new Promise((resolve, reject) => {
      this.http.get<Filtro>(this.api.get("filterTramiteParameter", [idTramite])).subscribe(
        (filter) => {
          resolve(filter);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  /**
   * Consulta una maestra sus columnas en especifico
   *
   * @param {EstructuraEspecifica} structure
   * @returns {Promise<ValoresEspecificos[]>}
   * @memberof FlowService
   */
  getColumnsSpecificMasterTable(structure: EstructuraEspecifica): Promise<ValoresEspecificos[]> {
    return new Promise((resolve, reject) => {
      const formData = {
        estructura: structure,
      };
      this.http.post<ValoresEspecificos[]>(this.api.get("masterTableSpecificInformation"), formData).subscribe(
        (specificValues) => {
          resolve(specificValues);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  /**
   * Genera la creacion de la secundaria
   *
   * @param {number} idTramite
   * @param {*} dataForm
   * @returns {Promise<boolean>}
   * @memberof FlowService
   */
  startSecondary(idTramite: number, dataForm: any): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.put<boolean>(this.api.get("instanceSecondaryWorkflow", [idTramite]), dataForm).subscribe(
        (res) => {
          resolve(true);
          this.toastr.success("Secundaria iniciada correctamente", "Operación exitosa");
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  /**
   * Cancelación de una secundaria
   *
   * @param {number} idSecondary
   * @param {*} reason
   * @returns {Promise<boolean>}
   * @memberof FlowService
   */
  cancelSecondary(idSecondary: number, reason: any): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const dataForm = {
        secundaria: idSecondary,
        motivo: reason,
      };
      this.http.post<boolean>(this.api.get("instanceSecondaryCancelation"), dataForm).subscribe(
        (res) => {
          this.toastr.success("Secundaria cancelada correctamente", "Operación exitosa");
          resolve(true);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  /**
   * retorna la informacion del semaforo con los tiempos del tramite
   *
   * @param {number} idTramite
   * @return {*}  {Observable<Etapas>}
   * @memberof FlowService
   */
  getStages(idTramite: number): Observable<Etapas> {
    return new Observable((obs) => {
      this.http.get<Etapas>(this.api.get("stageInstanceTimes", [idTramite])).subscribe(
        (stage) => {
          obs.next(stage);
        },
        (err) => {
          obs.error(err);
        }
      );
    });
  }

  
  /**
   *Consulta los parámetros de búsqueda para los selectores de filtro
   *
   * @param {number} process_id
   * @return {*}  {Observable<FiltrosSelector>}
   * @memberof FlowService
   */
  getParameters(process_id: number, procedure_id?: number): Observable<FiltrosSelector> {
    const body = {
      id_proceso: process_id
    }
    if(procedure_id){
      body['id_tramite'] = procedure_id;
    }
    return new Observable(obs=>{
      this.http.post<FiltrosSelector>(this.api.get('dynamicProcedureStructure'), body).subscribe(res=>{
        obs.next(res);
      }, err=>{
        obs.error(err);
      })
    })
  }

  /**
   *Consulta la lista de filtros para el parámetro de tipo en_lista
   *
   * @param {number} idFilter
   * @return {*} 
   * @memberof FlowService
   */
  getList(service_name: string): Promise<any[]>{
    const body = {
      nombre_servicio: service_name
    }
    return new Promise(resolve=>{
      this.http.post<any[]>(this.api.get('searchProcedureModalInfo'), body).subscribe(res=>{
        resolve(res)
      }, err=>{
        resolve(err);
      })
    })
  }
   
  /*Obtiene la información de anulación
   *
   * @param {number} idTramite
   * @memberof FlowService
   */
  getAnnulationInfo(idTramite: number): Observable<any>{
    return new Observable(obs => {
      this.http.get<any>(this.api.get('getAnnul', [idTramite])).subscribe(
        annulation => {
         obs.next(annulation);
        },
        err => {
          obs.error(err);
        }
      );
    });
  }

  /**
   *Obtiene la información del proceso y trámite (O solamente proceso)
   *
   * @param {number} process_id
   * @param {number} procedure_id
   * @return {*}  {Observable<any[]>}
   * @memberof FlowService
   */
  getDynamicProcedureData(process_id: number, procedure_id?: number, filters: any[] = []): Observable<any[]> {
    const body = {
      id_proceso: process_id,
      id_tramite: procedure_id,
      filtros: filters
    }
    return new Observable(obs=>{
      this.http.post<any[]>(this.api.get('searchDynamicProcedure'), body).subscribe(res=>{
        obs.next(res);
      }, err=>{
        obs.error(err);
      })
    })
  }

  /**
   *Servicio para descargar documento
   *
   * @param {number} idDocumento
   * @return {*}  {Observable<any>}
   * @memberof FlowService
   */
  dowloadFile(idDocumento: number): Observable<any> {
    return new Observable(obs => {
      this.http
        .skipErrorHandler()
        .get(this.api.get('docsInstance', [idDocumento]), {
          observe: 'response'
        })
        .subscribe(
          res => {
            obs.next(res.body);
          },
          err => {
            if (err.status !== 401 && err.status !== 403) {
              this.toastr.warning(err.error['message'], 'Fallo de descarga');
            }
            obs.error(err);
          }
        );
    });
  }

  /**
   *Servicio para descargar documento anexo
   *
   * @param {number} idDocumento
   * @return {*}  {Observable<any>}
   * @memberof FlowService
   */
  dowloadFileAnexed(id_anexed: number): Observable<any> {
    return new Observable(obs => {
      this.http
        .skipErrorHandler()
        .get(this.api.get('downloadAttachedDocument', [id_anexed]), {
          observe: 'response'
        })
        .subscribe(
          res => {
            obs.next(res.body);
          },
          err => {
            if (err.status !== 401 && err.status !== 403) {
              this.toastr.warning(err.error['message'], 'Fallo de descarga');
            }
            obs.error(err);
          }
        );
    });
  }


  sendDataService(data: DataService): Observable<DataService> {
    return new Observable(obs=>{
      this.http.post<DataService>(this.api.get('sendDataService'), data).subscribe(res=>{
        obs.next(res);
      }, err=>{
        if (err.status == 400 || err.status == 401) {
          this.toastr.warning(err.error, 'Fallo en el envio');
        }
        obs.error(err);
      })
    })
  }

  
} // End class



// WEBPACK FOOTER //
// ./src/app/flow/shared/flow.service.ts